/**
 * ServiceDetailsProvider Component
 * 
 * Context provider that manages the state and data for the service details modal.
 * Follows the same pattern as the ContactModalProvider for consistency.
 */
import React, { createContext, useState, useContext, ReactNode, useRef } from "react";

import { FeatureItem } from "../types/feature";

// Extended feature item interface with optional serviceKey
export interface ExtendedFeatureItem extends FeatureItem {
  serviceKey?: string;
}

interface ServiceDetailsContextType {
  /** Whether the service details modal is currently shown */
  isOpen: boolean;
  
  /** The currently selected service data */
  currentService: ExtendedFeatureItem | null;
  
  /** Function to open the service details modal with a specific service */
  openServiceDetails: (_service: ExtendedFeatureItem) => void;
  
  /** Function to close the service details modal */
  closeServiceDetails: () => void;
}

// Create the context with a default value
const ServiceDetailsContext = createContext<ServiceDetailsContextType | undefined>(undefined);

interface ServiceDetailsProviderProps {
  children: ReactNode;
}

// Map for service key lookup based on title
const SERVICE_KEY_MAP: Record<string, string> = {
  "AI Strategy & Implementation": "ai_strategy",
  "System & Process Automation": "automation",
  "Custom Development": "custom_development",
  "AI-Powered Analytics": "analytics",
  "Intelligent Automation": "intelligent_automation",
  "Data Integration & Engineering": "data_integration"
};

// Modal transition timing in ms
const MODAL_TRANSITION_TIME = 300;

/**
 * Provider component that makes the service details context available to any child component.
 */
export const ServiceDetailsProvider: React.FC<ServiceDetailsProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentService, setCurrentService] = useState<ExtendedFeatureItem | null>(null);
  const closingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isTransitioningRef = useRef<boolean>(false);

  // Helper to ensure service has a serviceKey for data lookups
  const validateServiceData = (service: ExtendedFeatureItem): ExtendedFeatureItem => {
    if (!service) return service;
    
    const validatedService = { ...service };
    
    // If service doesn't have a serviceKey, try to determine it from the title
    if (!validatedService.serviceKey && validatedService.title) {
      // Look up serviceKey from title mapping
      if (SERVICE_KEY_MAP[validatedService.title]) {
        validatedService.serviceKey = SERVICE_KEY_MAP[validatedService.title];
      } else {
        // Generate a fallback serviceKey from the title (for custom services)
        validatedService.serviceKey = validatedService.title
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '_')
          .replace(/_+/g, '_');
      }
    }
    
    return validatedService;
  };

  const openServiceDetails = (_service: ExtendedFeatureItem) => {
    // Clear any existing closing timer
    if (closingTimerRef.current) {
      clearTimeout(closingTimerRef.current);
      closingTimerRef.current = null;
    }
    
    // If we're in a transition, force immediate state updates
    if (isTransitioningRef.current) {
      isTransitioningRef.current = false;
    }
    
    // Validate and enhance the service data before storing
    const validatedService = validateServiceData(_service);
    
    // Update state
    setCurrentService(validatedService);
    setIsOpen(true);
  };

  const closeServiceDetails = () => {
    // Start closing transition
    isTransitioningRef.current = true;
    setIsOpen(false);
    
    // Clear service data after modal animation completes
    closingTimerRef.current = setTimeout(() => {
      setCurrentService(null);
      isTransitioningRef.current = false;
    }, MODAL_TRANSITION_TIME);
  };

  return (
    <ServiceDetailsContext.Provider
      value={{
        isOpen,
        currentService,
        openServiceDetails,
        closeServiceDetails,
      }}
    >
      {children}
    </ServiceDetailsContext.Provider>
  );
};

/**
 * Custom hook for accessing the service details context.
 * This follows the same pattern as other context hooks in the application.
 */
export const useServiceDetails = (): ServiceDetailsContextType => {
  const context = useContext(ServiceDetailsContext);
  
  if (context === undefined) {
    throw new Error("useServiceDetails must be used within a ServiceDetailsProvider");
  }
  
  return context;
};

export default ServiceDetailsProvider; 
